import React from "react";
import { clsx } from "clsx";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  className,
  id,
  ...props
}) => {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-ink-700 mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-700">
            {leftIcon}
          </div>
        )}
        <input
          id={inputId}
          className={clsx(
            "w-full px-4 py-2.5 text-sm border rounded-lg",
            // THE RING COLOUR LIVES IN THE TERNARY BELOW, NOT HERE — and that
            // is a fix, not a preference. `clsx` concatenates, it does not
            // dedupe, so an errored field used to carry BOTH
            // `focus:ring-primary-500` from this line and `focus:ring-error-500`
            // from the branch. Same specificity (one class + :focus), so the
            // winner is whichever Tailwind emits later — and it emits
            // error-500 first. The red error ring therefore never rendered.
            // Swapping this line to ink-950 would have reproduced the bug in
            // black: ink-950 also emits after error-500. Emitting exactly ONE
            // ring-colour class is the only order-independent fix.
            //
            // `focus:border-transparent` is gone for a second reason: it erased
            // the resting `border-neutral-300` edge, so a focused field was
            // LESS visible than an unfocused one while its replacement ring sat
            // at 1.07:1.
            "focus:outline-none focus:ring-2",
            "transition-all duration-200",
            error
              // 3.76:1 on white — over the 3:1 floor SC 1.4.11 sets for a focus
              // indicator, and it finally reaches the screen.
              ? "border-error-500 focus:ring-error-500"
              // #000000 on the white cards every one of the 16 call sites sits
              // on: 21:1, against 1.07:1 for the brand yellow it replaces.
              : "border-neutral-300 focus:ring-ink-950",
            leftIcon && "pl-10",
            rightIcon && "pr-10",
            className
          )}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-700">
            {rightIcon}
          </div>
        )}
      </div>
      {error && <p className="text-sm text-error-600 mt-1">{error}</p>}
      {hint && !error && <p className="text-sm text-neutral-700 mt-1">{hint}</p>}
    </div>
  );
};

export default Input;
